import React, {Fragment, useState} from 'react'
import PropTypes from 'prop-types'
import i18n from 'i18next'
import actions from 'redux-auto'
import Headline from '#components/a/Headline'
import Tooltip from '#components/m/Tooltip'
import Text from '#components/a/Text'
import Checkbox from '#components/a/form/Checkbox'
import Loading from '#components/a/Loading'
import Tag from '#components/a/Tag'
import ControlsGroup from '#components/m/ControlsGroup'
import Button from '#components/a/Button'
import {LoadingButton} from '#components/m/LoadingButton'
import {useAbteilungen} from '#hooks/store/useAbteilungen'
import useOnPressEscKey from '#hooks/onKeyPress/useOnPressEscKey'

function AbteilungenVerwaltenModalContent({ausbilder, closeModal, handleSave}) {
  const {abteilungen, isLoading} = useAbteilungen()
  const originallySelectedIds = ausbilder.abteilungen.map(a => a.id)
  const [selectedIds, setSelectedIds] = useState(originallySelectedIds.slice())
  const [isSaving, setIsSaving] = useState(false)
  const [warnMinimumAbteilungNeeded, setWarnMinimumAbteilungNeeded] = useState(false)

  const combinedAbteilungenDistinct = abteilungen
  ausbilder.abteilungen.forEach(element => {
    if (abteilungen.find(a => a.id === element.id) === undefined) {
      combinedAbteilungenDistinct.push(element)
      console.log(element.name)
    }
  })

  useOnPressEscKey(closeModal)

  const selectionCountText = () => {
    const count = selectedIds.length
    switch (count) {
      case 0:
        return i18n.t('meine_ausbilder_abteilungen_verwalten_selection_count_zero')
      case 1:
        return i18n.t('meine_ausbilder_abteilungen_verwalten_selection_count_one')
      default:
        return i18n.t('meine_ausbilder_abteilungen_verwalten_selection_count_multiple', [count])
    }
  }

  const toggleAbteilung = abteilungsId => {
    const currentlySelected = selectedIds.includes(abteilungsId)

    if (currentlySelected) {
      if (selectedIds.length === 1) {
        setWarnMinimumAbteilungNeeded(true)
        return
      }
      setSelectedIds(selectedIds.filter(id => id !== abteilungsId))
    } else {
      setWarnMinimumAbteilungNeeded(false)
      setSelectedIds(selectedIds.concat([abteilungsId]))
    }
  }

  const hasChanges = () => {
    if (selectedIds.length !== originallySelectedIds.length) {
      return true
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const id of selectedIds) {
      if (!originallySelectedIds.includes(id)) {
        return true
      }
    }
    return false
  }

  const handleConfirm = async () => {
    try {
      setIsSaving(true)
      await handleSave({
        ausbilderId: ausbilder.id,
        abteilungenIds: selectedIds,
      })
    } catch (e) {
      actions.error.writeError({error: e, errorMessage: i18n.t(e.message)})
    } finally {
      setIsSaving(false)
      closeModal()
    }
  }

  return (
    <div className="abteilungen-verwalten-modal" data-testid="abteilungen-verwalten-modal">
      <div className="abteilungen-verwalten-modal__header">
        <Headline modifier="article" importance={2} text={i18n.t('meine_ausbilder_abteilungen_verwalten')} />
        <Tooltip modifier={['dark', 'modal', 'nopadding']} placement="bottom" triggerAction="hover">
          <div className="abteilungen-verwalten-modal--tooltip">
            <Text modifier="extrasmall" text={i18n.t('meine_ausbilder_abteilungen_verwalten_info_tooltip')} />
          </div>
        </Tooltip>
      </div>

      {isLoading && <Loading />}
      {!isLoading && combinedAbteilungenDistinct && (
        <>
          {warnMinimumAbteilungNeeded && (
            <Text
              modifier={'warning'}
              text={i18n.t('meine_ausbilder_abteilungen_verwalten_selection_minimum_needed_info_text')}
            />
          )}

          <Text modifier="darker" text={selectionCountText()} />

          <ul className="abteilungen-verwalten-modal__tag-list">
            {combinedAbteilungenDistinct &&
              combinedAbteilungenDistinct.map(abteilung => {
                const isSelected = selectedIds.includes(abteilung.id)
                return (
                  <li key={abteilung.name}>
                    <Checkbox
                      disabledState={isSaving}
                      checked={isSelected}
                      onChangeHandle={() => toggleAbteilung(abteilung.id)}
                      labelText={
                        <Tag
                          name={abteilung.name}
                          color={abteilung.backgroundColor}
                          contrastingTextColor={abteilung.textColor}
                          variant={isSelected ? 'fill' : 'outline'}
                        />
                      }
                    />
                  </li>
                )
              })}
          </ul>
        </>
      )}
      <ControlsGroup>
        <Button disabled={isSaving} event={closeModal} text={i18n.t('misc_cancel')} variant="secondary" />
        <LoadingButton
          disabled={isSaving || !hasChanges()}
          isLoading={isSaving}
          event={handleConfirm}
          text={i18n.t('misc_save')}
          loadingText={i18n.t('misc_saving_pending')}
          variant="primary"
        />
      </ControlsGroup>
    </div>
  )
}

AbteilungenVerwaltenModalContent.propTypes = {
  closeModal: PropTypes.func,
  ausbilder: PropTypes.shape({
    id: PropTypes.number,
    abteilungen: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ),
  }),
  handleSave: PropTypes.func.isRequired,
}

export default AbteilungenVerwaltenModalContent
